import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { X, Search, Trash2, UserPlus, ClipboardList, ShieldOff } from 'lucide-react';

/**
 * Global reward exclusion-list manager.
 * One "do-not-send" list; any reward with the "Exclude list" toggle skips
 * everyone here. Add by player search or by pasting member numbers; remove
 * individually.
 */
export default function ExclusionListModal({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/reward-exclusions');
      setItems(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load exclusion list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { load(); setSearch(''); setResults([]); setPasteText(''); setReason(''); }
  }, [open, load]);

  // Debounced player search
  useEffect(() => {
    if (!open) return;
    if (!search || search.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/admin/users?search=${encodeURIComponent(search)}&limit=20`);
        setResults(res.data.users || res.data || []);
      } catch { /* ignore */ } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, open]);

  const excludedUserIds = new Set(items.filter(i => i.userId).map(i => String(i.userId)));

  const addUser = async (user) => {
    setBusy(true);
    try {
      const res = await api.post('/admin/reward-exclusions', {
        userIds: [user.id],
        reason: reason.trim() || undefined,
      });
      if (res.data.added > 0) toast.success('Added to exclusion list');
      else toast('Already on the list', { icon: 'ℹ️' });
      setSearch(''); setResults([]);
      load();
    } catch { toast.error('Failed to add'); } finally { setBusy(false); }
  };

  const addNumbers = async () => {
    const numbers = pasteText.split(/[\s,;\n\r\t]+/).map(s => s.trim()).filter(Boolean);
    if (!numbers.length) { toast.error('Paste one or more member numbers'); return; }
    setBusy(true);
    try {
      const res = await api.post('/admin/reward-exclusions', {
        memberNumbers: numbers,
        reason: reason.trim() || undefined,
      });
      const { added = 0, alreadyPresent = 0, unmatchedNumbers = [] } = res.data;
      toast.success(`Added ${added}${alreadyPresent ? `, ${alreadyPresent} already listed` : ''}`);
      if (unmatchedNumbers.length) {
        toast(`${unmatchedNumbers.length} number(s) had no linked account — still added by number`, { icon: '⚠️', duration: 6000 });
      }
      setPasteText('');
      load();
    } catch { toast.error('Failed to add numbers'); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/admin/reward-exclusions/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch { toast.error('Failed to remove'); } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <ShieldOff className="text-red-400" size={20} />
            <h2 className="text-white font-semibold">Reward Exclusion List</h2>
            <span className="text-xs text-gray-400">({items.length})</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <p className="text-xs text-gray-400">
            Members on this list are skipped by any reward or Quick Send that has the
            <span className="text-gray-200"> “Exclude list from reward” </span> toggle enabled.
          </p>

          {/* Optional reason applied to adds */}
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional, applied to items added below)"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
          />

          {/* Add by search */}
          <div>
            <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><UserPlus size={12} /> Add by player</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or member #"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-9 pr-3 py-2 text-white text-sm"
              />
            </div>
            {searching && <div className="text-xs text-gray-500 mt-1">Searching…</div>}
            {results.length > 0 && (
              <div className="mt-1 max-h-40 overflow-y-auto border border-gray-700 rounded-lg divide-y divide-gray-700">
                {results.map(u => {
                  const already = excludedUserIds.has(String(u.id));
                  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id;
                  const mn = u.memberNumber || u.playerClubMember?.memberNumber;
                  return (
                    <button
                      key={u.id}
                      disabled={already || busy}
                      onClick={() => addUser(u)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700 disabled:opacity-40 flex items-center justify-between"
                    >
                      <span className="text-gray-200">{name}{mn ? <span className="text-gray-500"> · #{mn}</span> : null}</span>
                      <span className="text-xs text-gray-500">{already ? 'on list' : 'add'}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add by bulk paste */}
          <div>
            <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><ClipboardList size={12} /> Add by member numbers (paste one per line or comma-separated)</label>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={3}
              placeholder="389001234&#10;389005678, 389009999"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm font-mono"
            />
            <button
              onClick={addNumbers}
              disabled={busy || !pasteText.trim()}
              className="mt-2 px-4 py-1.5 text-sm rounded-lg bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
            >
              Add numbers
            </button>
          </div>

          {/* Current list */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">On the list</label>
            {loading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-gray-500">No one is excluded yet.</div>
            ) : (
              <div className="border border-gray-700 rounded-lg divide-y divide-gray-700 max-h-56 overflow-y-auto">
                {items.map(it => (
                  <div key={it.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="text-gray-200 truncate">
                        {it.playerName || <span className="text-gray-500 italic">Unlinked member</span>}
                        {it.memberNumber ? <span className="text-gray-500"> · #{it.memberNumber}</span> : null}
                        {!it.hasAccount && <span className="ml-2 text-[10px] uppercase bg-amber-900/40 text-amber-300 px-1.5 py-0.5 rounded">no account</span>}
                      </div>
                      {it.reason ? <div className="text-xs text-gray-500 truncate">{it.reason}</div> : null}
                    </div>
                    <button onClick={() => remove(it.id)} disabled={busy} className="text-gray-500 hover:text-red-400 disabled:opacity-40 ml-2">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-white">Done</button>
        </div>
      </div>
    </div>
  );
}
